import { useEffect, useState } from 'react';
import { DownloadSimple, PlusCircle, Tag, Trash } from 'shared/icons';
import api from '../../../services/api';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Modal from '../../ui/Modal';
import { Mono } from '../../ui/Mono';
import Select from '../../ui/Select';
import { useToast } from '../../ui/Toast';
import { LocationPicker, ProductPicker, useActiveLocations } from '../lpnPickers';
import { DownloadZplFile, SendZplToPrinter, useZplPrinters, ZplPrinterSelect } from '../zplPrinting';

const DISPOSITIONS = ['SELLABLE', 'SUPPLIER_RMA'];

/**
 * SEPARAÇÃO da devolução: uma confirmação = UM pallet (LPN origin RETURN) com
 * os itens do MESMO destino. O DEPÓSITO não se escolhe aqui: vem da regra de
 * depósito do evento (CUSTOMER_RETURN_SELLABLE / RMA_SEND — configurável nas
 * Configurações do Armazém); a POSIÇÃO é livre, com a CONVENÇÃO da
 * devolução-boa (sector DEVOLUCAO) avisando quando não for seguida — aviso,
 * nunca trava (filosofia do M4/M5).
 */
const ReturnTriageModal = ({ isOpen, onClose, returnEntry, label, onCreated }) => {
    const toast = useToast();
    const [disposition, setDisposition] = useState('SELLABLE');
    const [locationId, setLocationId] = useState('');
    const [lines, setLines] = useState([{ product: null, qty: '1' }]);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const [created, setCreated] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [sending, setSending] = useState(false);

    const locations = useActiveLocations(isOpen);
    const { printers, localPrinters, selectedPrinter, setSelectedPrinter } = useZplPrinters(Boolean(created));

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setDisposition('SELLABLE');
        setLocationId('');
        setLines([{ product: null, qty: '1' }]);
        setCreated(null);
        setWarnings([]);
        setFormError(null);
    }, [isOpen]);

    const closeModal = () => {
        if (!saving && !sending) {
            onClose?.();
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFormError(null);
        if (!locationId) {
            setFormError({ message: `${label('wms.lpn.field.location')}: ${label('wms.common.required')}` });
            return;
        }
        const items = lines
            .filter((line) => line.product?.product_id && Number(line.qty) > 0)
            .map((line) => ({ product_id: String(line.product.product_id), qty: Number(line.qty) }));
        if (items.length === 0) {
            setFormError({ message: label('wms.lpn_create.items_required') });
            return;
        }

        setSaving(true);
        try {
            const data = await api.triageWarehouseReturn(returnEntry.id, {
                disposition,
                location_id: locationId,
                items,
            });
            setCreated(data?.lpn || null);
            setWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
            toast.success(label('wms.returns.triage_success'));
            onCreated?.();
        } catch (error) {
            setFormError({ message: error?.message || 'Erro ao salvar' });
        } finally {
            setSaving(false);
        }
    };

    const handlePrint = async () => {
        if (!created?.zpl || !selectedPrinter) {
            return;
        }
        setSending(true);
        try {
            const printerName = await SendZplToPrinter({ target: selectedPrinter, printers, zpl: created.zpl, label });
            toast.success(`${label('wms.labels.sent')}${printerName ? ` — ${printerName}` : ''}`);
        } catch (error) {
            toast.error(error);
        } finally {
            setSending(false);
        }
    };

    const createdStep = created ? (
        <div className="space-y-4">
            <div className="space-y-2 rounded-lg border border-mono-200 bg-mono-50 p-4 text-center dark:border-mono-800 dark:bg-mono-950">
                <p className="text-xs font-medium uppercase tracking-wide text-mono-500 dark:text-mono-400">
                    {label('wms.lpn_create.created_title')}
                </p>
                <Mono className="block text-2xl font-semibold text-black dark:text-white">{created.code}</Mono>
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                    <Badge variant={disposition === 'SELLABLE' ? 'success' : 'warning'}>
                        {label(`wms.returns.disposition.${disposition}`)}
                    </Badge>
                    {created.location_code ? <Badge variant="neutral">{created.location_code}</Badge> : null}
                    {created.deposit_code ? <Badge variant="neutral">{created.deposit_code}</Badge> : null}
                </div>
            </div>

            {warnings.length > 0 ? (
                <div className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/40">
                    {warnings.map((warning, index) => (
                        <p key={index} className="text-sm text-amber-800 dark:text-amber-200">
                            {warning.message}
                        </p>
                    ))}
                </div>
            ) : null}

            {created.zpl ? (
                <>
                    <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.lpn_create.print_hint')}</p>
                    <ZplPrinterSelect
                        label={label}
                        printers={printers}
                        localPrinters={localPrinters}
                        value={selectedPrinter}
                        onChange={setSelectedPrinter}
                        disabled={sending}
                    />
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Button
                            type="button"
                            onClick={handlePrint}
                            loading={sending}
                            disabled={!selectedPrinter}
                            className="flex-1"
                        >
                            <Tag size={18} className="mr-2" />
                            {label('wms.labels.send')}
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={sending}
                            onClick={() => DownloadZplFile(created.zpl, `lpn-${created.code}.zpl`)}
                        >
                            <DownloadSimple size={18} className="mr-2" />
                            {label('wms.labels.download')}
                        </Button>
                    </div>
                </>
            ) : null}

            <Button type="button" variant="secondary" onClick={closeModal} disabled={sending} className="w-full">
                {label('wms.lpn_create.finish')}
            </Button>
        </div>
    ) : null;

    const formStep = (
        <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.returns.triage_hint')}</p>
            <p className="rounded-lg border border-mono-200 bg-mono-50 px-3 py-2 text-xs text-mono-600 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400">
                {label('wms.returns.convention_note')}
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Select
                    label={label('wms.returns.disposition')}
                    value={disposition}
                    onChange={(event) => setDisposition(event.target.value)}
                    disabled={saving}
                >
                    {DISPOSITIONS.map((option) => (
                        <option key={option} value={option}>
                            {label(`wms.returns.disposition.${option}`)}
                        </option>
                    ))}
                </Select>
                <LocationPicker
                    label={label}
                    locations={locations.rows}
                    loading={locations.loading}
                    value={locationId}
                    onChange={setLocationId}
                    disabled={saving}
                />
            </div>

            <div className="space-y-2">
                {lines.map((line, index) => (
                    <div key={index} className="flex items-end gap-2">
                        <div className="min-w-0 flex-1">
                            <ProductPicker
                                label={label}
                                fieldLabel={label('wms.inbound.field.product')}
                                value={line.product}
                                onChange={(product) =>
                                    setLines((previous) =>
                                        previous.map((entry, i) => (i === index ? { ...entry, product } : entry)),
                                    )
                                }
                                disabled={saving}
                            />
                        </div>
                        <Input
                            type="number"
                            min="0.000001"
                            step="any"
                            label={label('wms.lpn.field.qty')}
                            value={line.qty}
                            onChange={(event) =>
                                setLines((previous) =>
                                    previous.map((entry, i) =>
                                        i === index ? { ...entry, qty: event.target.value } : entry,
                                    ),
                                )
                            }
                            disabled={saving}
                            className="w-28"
                        />
                        {lines.length > 1 ? (
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setLines((previous) => previous.filter((_entry, i) => i !== index))}
                                disabled={saving}
                            >
                                <Trash size={16} />
                            </Button>
                        ) : null}
                    </div>
                ))}
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={saving}
                    onClick={() => setLines((previous) => [...previous, { product: null, qty: '1' }])}
                >
                    <PlusCircle size={16} className="mr-2" />
                    {label('wms.lpn_create.add_item')}
                </Button>
            </div>

            {formError ? <p className="text-sm text-red-600 dark:text-red-400">{formError.message}</p> : null}

            <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={closeModal} disabled={saving}>
                    {label('wms.common.cancel')}
                </Button>
                <Button type="submit" loading={saving}>
                    {label('wms.returns.triage_confirm')}
                </Button>
            </div>
        </form>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={closeModal}
            title={
                created
                    ? label('wms.lpn_create.created_title')
                    : `${label('wms.returns.triage_title')} — ${returnEntry?.code || ''}`
            }
            size="xl"
        >
            {created ? createdStep : formStep}
        </Modal>
    );
};

export default ReturnTriageModal;
